/**
 * HeroSection Component
 * Hero banner with prospect name and status
 */

import React from 'react';

const styles = {
  hero: {
    padding: '80px 20px',
    background: 'linear-gradient(135deg, #0057FF 0%, #003D99 100%)',
    borderRadius: '8px',
    marginBottom: '40px',
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundDecorator: {
    position: 'absolute',
    opacity: 0.1,
    fontSize: '200px',
    fontWeight: '900',
    top: '-50px',
    right: '-50px',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  content: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '700px',
  },
  greeting: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#00D4AA',
    marginBottom: '12px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  clientName: {
    fontSize: '52px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '20px',
    lineHeight: '1.2',
  },
  statusPill: {
    display: 'inline-block',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '30px',
    marginTop: '12px',
  },
  statusActive: {
    backgroundColor: '#00D4AA',
    color: '#0F1729',
  },
  statusPending: {
    backgroundColor: '#FFB82D',
    color: '#0F1729',
  },
  statusExpired: {
    backgroundColor: '#FF6B6B',
    color: '#FFFFFF',
  },
  description: {
    fontSize: '18px',
    color: '#E0E0E0',
    lineHeight: '1.6',
    marginBottom: '12px',
  },
  cta: {
    marginTop: '40px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#00D4AA',
  },
};

/**
 * HeroSection Component
 * @param {Object} props
 * @param {Object} props.prospect - Prospect data
 */
function HeroSection({ prospect }) {
  if (!prospect) {
    return null;
  }

  const getStatusInfo = () => {
    const statusMap = {
      active: { label: 'Active', style: styles.statusActive },
      pending: { label: 'Pending Review', style: styles.statusPending },
      accepted: { label: 'Accepted', style: styles.statusActive },
      expired: { label: 'Expired', style: styles.statusExpired },
    };
    return statusMap[prospect.status] || statusMap.pending;
  };

  const statusInfo = getStatusInfo();
  const industryText = prospect.industry ? `in ${prospect.industry}` : '';

  return (
    <div style={styles.hero}>
      <div style={styles.backgroundDecorator}>📊</div>
      <div style={styles.content}>
        <div style={styles.greeting}>Welcome to your custom portal</div>
        <h1 style={styles.clientName}>{prospect.clientName || 'Valued Client'}</h1>
        <div style={{ ...styles.statusPill, ...statusInfo.style }}>
          {statusInfo.label}
        </div>
        <p style={styles.description}>
          We've prepared a custom proposal {industryText} designed specifically for your business.
        </p>
        <div style={styles.cta}>
          Scroll down to explore your personalized proposal and next steps →
        </div>
      </div>
    </div>
  );
}

HeroSection.propTypes = {
  prospect: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'object') {
      return new Error(`${propName} must be an object`);
    }
  },
};

export default HeroSection;
